import { useCallback, useState } from 'react';
import type { Admin } from '@london-life/contracts';
import { Badge } from '@london-life/ui/components/badge';
import { Button } from '@london-life/ui/components/button';
import { Input } from '@london-life/ui/components/input';
import { Label } from '@london-life/ui/components/label';
import { CheckList } from '@london-life/ui/admin/check-list';
import { ConfirmDialog } from '@london-life/ui/admin/confirm-dialog';
import { DataTable } from '@london-life/ui/admin/data-table';
import { FormDialog } from '@london-life/ui/admin/form-dialog';
import { PageHeader } from '@london-life/ui/admin/page-header';
import { ErrorNotice } from '@london-life/ui/feedback/error-notice';
import { api } from '../lib/api';
import { useRequest } from '../lib/use-request';

// super admin: add admins from accounts that have signed in, change their roles, disable them
export function AdminsPage() {
  const admins = useRequest(useCallback(() => api.admin.listAdmins(), []));
  const roles = useRequest(useCallback(() => api.admin.listRoles(), []));
  const roleOptions = (roles.data?.roles ?? []).map((role) => ({
    value: role.id,
    label: role.name,
    hint: role.description,
  }));
  const [adding, setAdding] = useState(false);
  const [email, setEmail] = useState('');
  const [newRoles, setNewRoles] = useState<string[]>([]);
  const [editing, setEditing] = useState<{ admin: Admin; roleIds: string[] } | null>(null);
  const [disabling, setDisabling] = useState<Admin | null>(null);
  const [resetting, setResetting] = useState<Admin | null>(null);

  return (
    <section>
      <PageHeader
        title="Admins"
        description="People who can sign in to this dashboard, and what each of them can do."
        actions={<Button onClick={() => setAdding(true)}>Add admin</Button>}
      />
      {admins.error && <ErrorNotice error={admins.error} onRetry={() => void admins.reload()} />}
      <DataTable
        rows={admins.data?.admins ?? null}
        loading={admins.busy}
        rowKey={(admin) => admin.id}
        empty="No admins yet."
        columns={[
          {
            key: 'account',
            header: 'Account',
            render: (admin) => (
              <>
                <strong>{admin.user.displayName ?? '—'}</strong>
                <div className="admin-muted">{admin.user.email}</div>
              </>
            ),
          },
          {
            key: 'roles',
            header: 'Roles',
            render: (admin) =>
              admin.superAdmin ? (
                <Badge>Super admin</Badge>
              ) : (
                <div className="admin-tags">
                  {admin.roles.map((role) => (
                    <Badge key={role.id} variant="secondary">
                      {role.name}
                    </Badge>
                  ))}
                </div>
              ),
          },
          {
            key: 'status',
            header: 'Status',
            render: (admin) =>
              admin.disabledAt ? <Badge variant="outline">Disabled</Badge> : <Badge variant="secondary">Active</Badge>,
          },
          {
            key: 'actions',
            header: '',
            className: 'text-right',
            render: (admin) =>
              !admin.superAdmin &&
              !admin.disabledAt && (
                <div className="admin-page-actions justify-end">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setEditing({ admin, roleIds: admin.roles.map((role) => role.id) })}
                  >
                    Edit roles
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setResetting(admin)}>
                    Reset two-step
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setDisabling(admin)}>
                    Disable
                  </Button>
                </div>
              ),
          },
        ]}
      />

      <FormDialog
        open={adding}
        title="Add an admin"
        description="The person must have signed in to London Life once with this email address."
        submitLabel="Add admin"
        canSubmit={Boolean(email) && newRoles.length > 0}
        onClose={() => setAdding(false)}
        onSubmit={async () => {
          await api.admin.createAdmin({ email, roleIds: newRoles });
          setEmail('');
          setNewRoles([]);
          await admins.reload();
        }}
      >
        <div className="admin-field">
          <Label htmlFor="admin-email">Email</Label>
          <Input
            id="admin-email"
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            required
          />
        </div>
        <div className="admin-field">
          <Label>Roles</Label>
          <CheckList options={roleOptions} selected={newRoles} onChange={setNewRoles} />
        </div>
      </FormDialog>

      <FormDialog
        open={editing !== null}
        title={`Roles for ${editing?.admin.user.displayName ?? editing?.admin.user.email ?? ''}`}
        description="Saving signs this admin out everywhere, so the new permissions apply straight away."
        submitLabel="Save roles"
        canSubmit={Boolean(editing?.roleIds.length)}
        onClose={() => setEditing(null)}
        onSubmit={async () => {
          if (!editing) return;
          await api.admin.setAdminRoles(editing.admin.id, { roleIds: editing.roleIds });
          await admins.reload();
        }}
      >
        <CheckList
          options={roleOptions}
          selected={editing?.roleIds ?? []}
          onChange={(roleIds) => editing && setEditing({ ...editing, roleIds })}
        />
      </FormDialog>

      <ConfirmDialog
        open={resetting !== null}
        title="Reset two-step login?"
        description={`Use this when ${resetting?.user.email ?? 'this admin'} has lost their phone and their recovery codes. They are signed out everywhere and set up two-step login again at their next sign-in.`}
        confirmLabel="Reset two-step login"
        danger
        onClose={() => setResetting(null)}
        onConfirm={async () => {
          if (!resetting) return;
          await api.admin.twoStep.resetFor(resetting.id);
        }}
      />

      <ConfirmDialog
        open={disabling !== null}
        title="Disable this admin?"
        description={`${disabling?.user.email ?? 'This admin'} will be signed out everywhere and can no longer use the dashboard. Their player account is not affected.`}
        confirmLabel="Disable admin"
        danger
        onClose={() => setDisabling(null)}
        onConfirm={async () => {
          if (!disabling) return;
          await api.admin.disableAdmin(disabling.id);
          await admins.reload();
        }}
      />
    </section>
  );
}
